import React, { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { Client } from "@microsoft/microsoft-graph-client";
import {
  Typography,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Chip,
  ListItemAvatar,
  Avatar,
  CircularProgress,
  Tooltip
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { AppRole } from "@microsoft/microsoft-graph-types";

/** Extended details about a user assigned to the app. */
interface ProvisionedUser {
  id: string;
  assignmentId: string;
  resourceId: string;
  principalId: string;
  displayName?: string;
  userPrincipalName?: string;
  assignedRoleDisplayName?: string;
  assignedRoleValue?: string;
}

/** Props for the UserProvisioning component. */
interface UserProvisioningProps {
  /** The Azure AD Application (client) ID. */
  appId: string;
  /** The optional servicePrincipalId if known. Otherwise, the code looks it up. */
  servicePrincipalId?: string;
}

/**
 * Read-only user provisioning component that displays current user assignments
 */
const UserProvisioning: React.FC<UserProvisioningProps> = ({
  appId,
  servicePrincipalId,
}) => {
  const { instance, accounts } = useMsal();

  // State for user assignments
  const [provisionedUsers, setProvisionedUsers] = useState<ProvisionedUser[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // Panel expansion
  const [usersExpanded, setUsersExpanded] = useState(true);

  // The roles available in this SP
  const [appRoles, setAppRoles] = useState<AppRole[]>([]);

  /*******************************
   *       MS GRAPH HELPERS
   *******************************/
  /**
   * Gets a Graph access token with the required scopes.
   */
  async function getGraphAccessToken(): Promise<string> {
    const accessTokenRequest = {
      scopes: [
        "Directory.Read.All"
      ],
      account: accounts[0]
    };

    try {
      const response = await instance.acquireTokenSilent(accessTokenRequest);
      return response.accessToken;
    } catch (err: any) {
      if (err instanceof InteractionRequiredAuthError) {
        const response = await instance.acquireTokenPopup(accessTokenRequest);
        return response.accessToken;
      }
      throw err;
    }
  }

  /**
   * Fetch all assigned users for this SP, plus the roles so we can display them.
   */
  async function fetchProvisionedUsers() {
    setLoading(true);
    try {
      const token = await getGraphAccessToken();
      await fetchUsers(token);
    } catch (error) {
      console.error("Failed to fetch provisioned users:", error);
    } finally {
      setLoading(false);
    }
  }

  /**
   * Actually calls Graph for assigned users and role data.
   */
  async function fetchUsers(accessToken: string) {
    const client = Client.init({ authProvider: (done) => done(null, accessToken) });

    const spId = servicePrincipalId || (await getServicePrincipalId(client));
    if (!spId) {
      console.error("Service principal not found for the application.");
      return;
    }

    // 1) Get appRoleAssignedTo
    let allAssignments: any[] = [];
    let resp = await client.api(`/servicePrincipals/${spId}/appRoleAssignedTo`).get();
    while (resp) {
      allAssignments = [...allAssignments, ...resp.value];
      if (resp["@odata.nextLink"]) {
        resp = await client.api(resp["@odata.nextLink"]).get();
      } else {
        break;
      }
    }

    // 2) Get roles from the SP so we can map IDs to role names
    const spObj = await client.api(`/servicePrincipals/${spId}`).get();
    const spRoles: AppRole[] = spObj.appRoles || [];
    const rolesMap: Record<string, AppRole> = {};
    spRoles.forEach((role) => {
      if (role.id) {
        rolesMap[role.id] = role;
      }
    });

    // 3) For each assignment that is for a user, fetch user details and role
    const allUsers: ProvisionedUser[] = [];
    for (const assignment of allAssignments) {
      if (assignment.principalType === "User") {
        try {
          const userRes = await client.api(`/users/${assignment.principalId}`).get();
          const roleInfo = rolesMap[assignment.appRoleId] || {};
          allUsers.push({
            id: userRes.id,
            assignmentId: assignment.id,
            resourceId: assignment.resourceId,
            principalId: userRes.id,
            displayName: userRes.displayName,
            userPrincipalName: userRes.userPrincipalName,
            assignedRoleDisplayName: roleInfo.displayName || undefined,
            assignedRoleValue: roleInfo.value || undefined
          });
        } catch (userError) {
          console.warn(`Failed to fetch user details for assignment ${assignment.id}:`, userError);
        }
      }
    }
    setProvisionedUsers(allUsers);
  }

  /**
   * Looks up the servicePrincipal ID for the given appId.
   */
  async function getServicePrincipalId(client: Client): Promise<string | null> {
    const spRes = await client.api(`/servicePrincipals?$filter=appId eq '${appId}'`).get();
    if (spRes.value && spRes.value.length > 0) {
      return spRes.value[0].id;
    }
    return null;
  }

  /*******************************
   *        useEffects
   *******************************/
  useEffect(() => {
    if (appId) {
      fetchProvisionedUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId]);

  useEffect(() => {
    async function fetchAppRoles() {
      try {
        const token = await getGraphAccessToken();
        const client = Client.init({ authProvider: (done) => done(null, token) });

        const spId = servicePrincipalId || (await getServicePrincipalId(client));
        if (!spId) {
          console.error("Service Principal not found.");
          return;
        }

        const spObj = await client.api(`/servicePrincipals/${spId}`).get();
        const roles = spObj.appRoles?.filter((role: AppRole) => role.isEnabled) || [];
        setAppRoles(roles);
      } catch (error) {
        console.error("Error fetching app roles:", error);
      }
    }

    if (servicePrincipalId || appId) {
      fetchAppRoles();
    }
  }, [servicePrincipalId, appId, accounts, instance]);

  const handleUsersAccordionChange = (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setUsersExpanded(isExpanded);
  };

  /*******************************
   *         RENDER UI
   *******************************/
  return (
    <Card variant="outlined" sx={{ mb: 4 }}>
      <CardContent>
        {/* PROVISIONED USERS - Updated with indigo theme like other read-only components */}
        <Accordion 
          expanded={usersExpanded}
          onChange={handleUsersAccordionChange}
          sx={{
            border: '1px solid #e0e7ff', // Indigo border like other components
            borderRadius: '8px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            mb: 2
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              background: 'linear-gradient(135deg, #e8eaf6 0%, #f3e5f5 100%)',
              borderBottom: usersExpanded ? '1px solid #e0e7ff' : 'none',
              minHeight: '64px'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Box sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: '#3f51b5', // Indigo background
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2
              }}>
                <GroupIcon sx={{ color: 'white', fontSize: '1.2rem' }} />
              </Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                  fontSize: "1.25rem",
                  fontWeight: 600,
                  color: "#1a237e", // Indigo text color
                  letterSpacing: "0.02em",
                  flexGrow: 1
                }}
              >
                User Provisioning
              </Typography>
              <Chip 
                size="small"
                label={`${provisionedUsers.length} users`}
                sx={{ 
                  mr: 1,
                  backgroundColor: '#e3f2fd',
                  color: '#1565c0',
                  fontWeight: 500,
                  border: '1px solid #bbdefb'
                }}
              />
              {(servicePrincipalId || appId) && (
                <Tooltip title="Configure in Entra Portal">
                  <IconButton
                    size="small"
                    component="a"
                    href={
                      servicePrincipalId
                        ? `https://portal.azure.com/#view/Microsoft_AAD_IAM/ManagedAppMenuBlade/~/Users/objectId/${servicePrincipalId}/appId/${appId}`
                        : `https://portal.azure.com/#view/Microsoft_AAD_IAM/ManagedAppMenuBlade/~/Users/appId/${appId}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    sx={{ ml: 1, color: '#5c6bc0' }}
                  >
                    <OpenInNewIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </AccordionSummary>
          
          <AccordionDetails sx={{ 
            p: 0,
            backgroundColor: "#fafbff" // Light indigo background
          }}>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            )}

            {!loading && provisionedUsers.length === 0 ? (
              <Box sx={{ 
                p: 4, 
                m: 2,
                backgroundColor: '#f8f9fa', 
                borderRadius: '12px', 
                textAlign: 'center',
                border: '2px dashed #dee2e6'
              }}>
                <Box sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  backgroundColor: '#e9ecef',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2
                }}>
                  <GroupIcon sx={{ fontSize: '2rem', color: '#6c757d' }} />
                </Box>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                  No users provisioned
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This application does not have any users assigned to it.
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 2 }}>
                {provisionedUsers.map((user, index) => (
                  <ListItem
                    key={user.assignmentId}
                    sx={{
                      mb: 2,
                      p: 2,
                      borderRadius: '8px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
                      backgroundColor: 'white',
                      borderLeft: '4px solid #3f51b5' // Indigo left border
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: '#e8eaf6' }}>
                        <PersonIcon sx={{ color: '#3f51b5' }} />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                          {user.displayName || user.userPrincipalName}
                        </Typography>
                      }
                      secondary={
                        <Stack spacing={0.5} sx={{ mt: 1 }}>
                          <Typography component="span" variant="body2" color="text.secondary">
                            <strong>UPN:</strong> {user.userPrincipalName}
                          </Typography>
                          {user.assignedRoleValue && (
                            <Box sx={{ mt: 0.5 }}>
                              <Chip 
                                label={`Role: ${user.assignedRoleValue}`}
                                size="small"
                                sx={{
                                  backgroundColor: '#e8eaf6',
                                  color: '#3f51b5',
                                  fontWeight: 500,
                                  border: '1px solid #c5cae9'
                                }}
                              />
                            </Box>
                          )}
                        </Stack>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}

            {/* About User Provisioning */}
            <Box sx={{
              m: 2,
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 1px 4px rgba(0, 0, 0, 0.04)',
              opacity: 0.85
            }}>
              <Accordion sx={{ '&:before': { display: 'none' }, borderRadius: '8px !important' }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ fontSize: '1.2rem', color: '#9e9e9e' }} />}
                  sx={{
                    background: '#f8f9fa',
                    borderBottom: '1px solid #e9ecef',
                    minHeight: '48px',
                    '&:hover': { backgroundColor: '#f1f3f4' }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <InfoOutlinedIcon sx={{ mr: 1.5, fontSize: "1.1rem", color: 'info.main' }} />
                    <Typography variant="subtitle1" sx={{ fontSize: "1rem", fontWeight: 500 }}>
                      About User Provisioning
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ p: 2, backgroundColor: "#f5f5ff", borderRadius: 1 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      User provisioning determines which users can access your application and what roles they have within it.
                    </Typography>
                    
                    <Box sx={{ 
                      p: 2, 
                      backgroundColor: '#ffffff', 
                      border: '1px solid #e0e0ff', 
                      borderRadius: 1,
                      mb: 2
                    }}>
                      <Typography variant="subtitle2" sx={{ color: '#0d47a1', mb: 1 }}>
                        How User Assignment Works:
                      </Typography>
                      <Typography variant="body2">
                        • <strong>Enterprise Application Assignment:</strong> Users must be explicitly assigned to the enterprise application<br />
                        • <strong>App Role Assignment:</strong> Each user is assigned a specific app role that determines their permissions<br />
                        • <strong>Token Claims:</strong> When users sign in, their assigned roles appear in the `roles` claim of their tokens<br />
                        • <strong>Access Control:</strong> Your application can use these role claims to control user access and features
                      </Typography>
                    </Box>
                    
                    <Box sx={{
                      p: 2,
                      backgroundColor: '#fff8e1',
                      border: '1px solid #ffe082',
                      borderRadius: 1
                    }}>
                      <Typography variant="subtitle2" sx={{ color: '#bf360c', mb: 1 }}>
                        Management Notes:
                      </Typography>
                      <Typography variant="body2">
                        • User assignments can be managed through the Azure Portal under Enterprise Applications<br />
                        • Users must exist in your Entra ID tenant before they can be assigned<br />
                        • App roles must be defined in the application registration before assignment<br />
                        • Removing an assignment revokes the user's access but doesn't delete their account
                      </Typography>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  );
};

export default UserProvisioning;